import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
} from "@material-ui/core";
import {
  Description,
  GetApp,
  OpenInNew,
  Close,
  PictureAsPdf,
  Image,
  VideoLibrary,
  Audiotrack,
  Archive,
  Code,
  TableChart,
  Slideshow,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { getBackendUrl } from "../../config";
import { toast } from "react-toastify";

/** Mesma lógica de ChatMessages: evita URL relativa no host do React (SPA “engole” /public/...). */
function resolveDocumentMediaUrl(msg) {
  const raw = msg?.mediaPath || msg?.mediaUrl;
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("blob:")) return s;
  const base = (getBackendUrl() || "").replace(/\/$/, "");
  if (s.startsWith("/public")) return base ? `${base}${s}` : s;
  const cid = msg?.companyId;
  if (cid != null && !/[\\/]/.test(s) && !s.includes("..")) {
    return `${base}/public/company${cid}/chat/${s}`;
  }
  if (base && s.startsWith("/")) return `${base}${s}`;
  return s;
}

/** Backend força attachment em PDF; download=0 libera visualização inline. */
function withInlineViewParam(url) {
  if (!url || String(url).startsWith("blob:")) return url;
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("download", "0");
    return u.toString();
  } catch {
    const sep = String(url).includes("?") ? "&" : "?";
    return `${url}${sep}download=0`;
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      minWidth: 400,
      maxWidth: 900,
    },
  },
  documentInfo: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  documentIcon: {
    fontSize: 48,
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  documentDetails: {
    flex: 1,
  },
  documentName: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  documentType: {
    marginBottom: theme.spacing(1),
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
  downloadButton: {
    marginRight: theme.spacing(1),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  pdfFrame: {
    width: "100%",
    minHeight: 360,
    maxHeight: "65vh",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
  },
}));

const DocumentModal = ({ open, onClose, document: documentMessage }) => {
  const classes = useStyles();
  const [fileInfo, setFileInfo] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  useEffect(() => {
    if (documentMessage && (documentMessage.mediaPath || documentMessage.mediaUrl)) {
      const fileName =
        documentMessage.mediaName ||
        documentMessage.mediaPath?.split("/").pop() ||
        documentMessage.mediaUrl?.split("/").pop() ||
        "";
      const cleanName = String(fileName).split("?")[0];
      const fileExtension = cleanName.split(".").pop()?.toLowerCase();

      setFileInfo({
        name: cleanName,
        extension: fileExtension,
        type: documentMessage.mediaType,
        url: resolveDocumentMediaUrl(documentMessage),
      });
    } else {
      setFileInfo(null);
    }
  }, [documentMessage]);

  // PDF: busca blob com download=0 para o iframe não disparar download forçado
  useEffect(() => {
    if (!open || !fileInfo?.url || fileInfo.extension !== "pdf") {
      setPdfPreviewUrl(null);
      setPdfError(false);
      setPdfLoading(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;

    const loadPdf = async () => {
      setPdfLoading(true);
      setPdfError(false);
      setPdfPreviewUrl(null);
      try {
        const inlineUrl = withInlineViewParam(fileInfo.url);
        const res = await fetch(inlineUrl, {
          credentials: "include",
          mode: "cors",
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        const pdfBlob =
          blob.type === "application/pdf"
            ? blob
            : new Blob([blob], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(pdfBlob);
        if (!cancelled) {
          setPdfPreviewUrl(objectUrl);
          setPdfLoading(false);
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch (e) {
        console.error("Erro ao carregar preview do PDF:", e);
        if (!cancelled) {
          // Fallback: URL com download=0 (alguns browsers ainda renderizam)
          setPdfPreviewUrl(withInlineViewParam(fileInfo.url));
          setPdfError(true);
          setPdfLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, fileInfo?.url, fileInfo?.extension]);

  const getDocumentIcon = (extension, mediaType) => {
    switch (extension) {
      case "pdf":
        return <PictureAsPdf className={classes.documentIcon} />;
      case "doc":
      case "docx":
      case "odt":
        return <Description className={classes.documentIcon} />;
      case "xls":
      case "xlsx":
      case "ods":
        return <TableChart className={classes.documentIcon} />;
      case "ppt":
      case "pptx":
      case "odp":
        return <Slideshow className={classes.documentIcon} />;
      case "zip":
      case "rar":
      case "7z":
      case "tar":
      case "gz":
      case "bz2":
        return <Archive className={classes.documentIcon} />;
      case "exe":
        return <GetApp className={classes.documentIcon} />;
      case "xml":
      case "json":
      case "html":
        return <Code className={classes.documentIcon} />;
      case "ret":
        return <Description className={classes.documentIcon} />;
      default:
        if (mediaType === "image")
          return <Image className={classes.documentIcon} />;
        if (mediaType === "video")
          return <VideoLibrary className={classes.documentIcon} />;
        if (mediaType === "audio")
          return <Audiotrack className={classes.documentIcon} />;
        return <Description className={classes.documentIcon} />;
    }
  };

  const getDocumentTypeName = (extension) => {
    const typeMap = {
      pdf: "Documento PDF",
      doc: "Documento Word",
      docx: "Documento Word",
      odt: "Documento OpenDocument",
      xls: "Planilha Excel",
      xlsx: "Planilha Excel",
      ods: "Planilha OpenDocument",
      ppt: "Apresentação PowerPoint",
      pptx: "Apresentação PowerPoint",
      odp: "Apresentação OpenDocument",
      zip: "Arquivo Compactado",
      rar: "Arquivo Compactado",
      "7z": "Arquivo Compactado",
      tar: "Arquivo Compactado",
      gz: "Arquivo Compactado",
      bz2: "Arquivo Compactado",
      xml: "Arquivo XML",
      json: "Arquivo JSON",
      html: "Arquivo HTML",
      ofx: "Arquivo OFX",
      msg: "E-mail Outlook",
      key: "Apresentação Keynote",
      numbers: "Planilha Numbers",
      pages: "Documento Pages",
      exe: "Executável",
      ret: "Arquivo RET",
    };

    return typeMap[extension] || "Documento";
  };

  const downloadBlob = useCallback(async () => {
    if (!fileInfo?.url) return;
    try {
      const res = await fetch(fileInfo.url, {
        credentials: "include",
        mode: "cors",
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = blobUrl;
      a.download = fileInfo.name || "documento";
      a.rel = "noopener";
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível baixar. Tente abrir em nova aba.");
    }
  }, [fileInfo]);

  const handleDownload = () => {
    downloadBlob();
  };

  /** Abre o arquivo no navegador (nova aba) em modo visualização (sem forçar download). */
  const handleOpen = () => {
    if (!fileInfo?.url) return;
    const url =
      fileInfo.extension === "pdf"
        ? withInlineViewParam(fileInfo.url)
        : fileInfo.url;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!fileInfo) return null;

  const isPdf = fileInfo.extension === "pdf";

  return (
    <Dialog open={open} onClose={onClose} className={classes.root} maxWidth={isPdf ? "md" : "sm"} fullWidth>
      <DialogTitle>
        Visualizar Documento
        <IconButton className={classes.closeButton} onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isPdf && fileInfo.url ? (
          <>
            <Box mb={2}>
              {pdfLoading ? (
                <Box
                  className={classes.pdfFrame}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography variant="body2" color="textSecondary">
                    Carregando PDF…
                  </Typography>
                </Box>
              ) : pdfPreviewUrl ? (
                <iframe
                  title={fileInfo.name}
                  src={`${pdfPreviewUrl}#toolbar=1`}
                  className={classes.pdfFrame}
                />
              ) : (
                <Box
                  className={classes.pdfFrame}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography variant="body2" color="textSecondary">
                    {pdfError
                      ? "Não foi possível pré-visualizar. Use Abrir em nova aba."
                      : "PDF indisponível"}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box className={classes.documentDetails}>
              <Typography className={classes.documentName} variant="h6">
                {fileInfo.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {getDocumentTypeName(fileInfo.extension)}
              </Typography>
              <Chip
                label={fileInfo.extension.toUpperCase()}
                size="small"
                color="primary"
                variant="outlined"
                style={{ marginTop: 8 }}
              />
            </Box>
          </>
        ) : (
          <Box className={classes.documentInfo}>
            {["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(
              fileInfo.extension
            ) ? (
              <img
                src={fileInfo.url}
                alt={fileInfo.name}
                style={{ maxWidth: "100%", maxHeight: 300, marginBottom: 16 }}
              />
            ) : (
              getDocumentIcon(fileInfo.extension, fileInfo.type)
            )}
            <Box className={classes.documentDetails}>
              <Typography className={classes.documentName} variant="h6">
                {fileInfo.name}
              </Typography>
              <Typography
                className={classes.documentType}
                variant="body2"
                color="textSecondary"
              >
                {getDocumentTypeName(fileInfo.extension)}
              </Typography>
              <Chip
                label={fileInfo.extension.toUpperCase()}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<OpenInNew />}
            onClick={handleOpen}
            className={classes.downloadButton}
          >
            {isPdf ? "Abrir em nova aba" : "Abrir"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetApp />}
            onClick={handleDownload}
          >
            Download
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentModal;
